
import { useEffect, useState } from "react";
import { Autocomplete, Box, Button, FormControl, FormGroup, FormLabel, Modal, TextField, Typography } from "@mui/material";
import { Addon, Category, Material, Product, ProductOption, Size } from "./ProductType";
import { createProduct, updateProduct } from "services/ProductService";
import ShireImageUpload from "components/ShireImageUpload";
import ShireImageCard from "components/ShireImageCard";
import { uploadImage } from "services/FileService";

interface ProductModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void>;
  initialValues: Product;
  isEdit: boolean;
  categories: ProductOption[];
  sizes: ProductOption[];
  materials: ProductOption[];
  addons: ProductOption[];
}

export const ProductModal: React.FC<ProductModalProps> = ({ open, onClose, onSubmit, initialValues, isEdit, categories, sizes, materials, addons }) => {
  const [product, setProduct] = useState<Product>({
    ...initialValues
  });
  const [imageFile, setImageFile] = useState<File | null>(null);


  useEffect(() => {
    if (open) {
      setProduct({ ...initialValues });
    }
  }, [open, initialValues]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProduct({ ...product, [e.target.name]: e.target.value });
  };

  const handleAddPhoto = async () => {
    if (!imageFile) return;

    const formData = new FormData();
    formData.append("image", imageFile);

    const { url, public_id: publicId } = await uploadImage(formData, 'products');
    setProduct({ ...product, photos: [...product.photos, {
      id: 0,
      url,
      externalId: publicId,
      isActive: true,
    }]});
    setImageFile(null);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isEdit) {
      await updateProduct({
        id: product.id,
        name: product.name,
        description: product.description,
        basePrice: product.basePrice,
        categoryIds: product.categories.map((category) => category.id),
        sizes: product.sizes,
        materials: product.materials,
        photos: product.photos,
        addons: product.addons,
      });
    } else {
      await createProduct({
        name: product.name,
        description: product.description,
        basePrice: product.basePrice,
        categoryIds: product.categories.map((category) => category.id),
        sizes: product.sizes,
        materials: product.materials,
        photos: product.photos,
        addons: product.addons,
      });
    }

    await onSubmit();
    setImageFile(null);
  };

  return <Modal open={open} onClose={onClose}>
    <Box
      component="form"
      sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '80vw', bgcolor: 'background.paper', boxShadow: 24, p: 4, overflow: 'auto', maxHeight: '90vh' }}
      onSubmit={handleSubmit}
    >
      <Typography variant="h6">{isEdit ? 'Edit Product' : 'Add Product'}</Typography>
      
      {/* Name and Description row */}
      <FormGroup sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 2 }}>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel htmlFor="name">Tên sản phẩm</FormLabel>
          <TextField
            id="name"
            name="name"
            placeholder="Tên sản phẩm"
            autoComplete="name"
            autoFocus
            value={product.name}
            onChange={handleChange}
          />
        </FormControl>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel htmlFor="description">Mô tả</FormLabel>
          <TextField
            id="description"
            name="description"
            placeholder="Mô tả sản phẩm"
            autoComplete="description"
            value={product.description}
            onChange={handleChange}
          />
        </FormControl>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel htmlFor="basePrice">Giá cơ bản</FormLabel>
          <TextField
            id="basePrice"
            name="basePrice"
            placeholder="Giá cơ bản"
            autoComplete="basePrice"
            value={product.basePrice}
            onChange={handleChange}
          />
        </FormControl>
      </FormGroup>
      <FormControl sx={{ mt: 2, width: '100%' }}>
        <FormLabel htmlFor="categories">Danh mục</FormLabel>
        <Autocomplete
          multiple
          options={categories}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => <TextField {...params} placeholder="Chọn danh mục" />}
          value={product.categories.map((category) => ({
            id: category.id,
            name: category.name,
            price: 0,
            code: category.code,
          }))}
          onChange={(event, value) => {
            setProduct({ ...product, categories: value.map((option: ProductOption) => ({
              id: option.id,
              name: option.name,
              description: '',
              code: option.code,
              isActive: true,
            })) as Category[] });
          }}
        />
      </FormControl>
      <FormControl sx={{ mt: 2, width: '100%' }}>
        <FormLabel htmlFor="sizes">Kích thước</FormLabel>
        <Autocomplete
          multiple
          options={sizes}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => <TextField {...params} placeholder="Chọn kích thước" />}
          value={product.sizes.map((size) => ({
            id: size.id,
            name: size.name,
            price: size.price,
            code: '',
          }))}
          onChange={(event, value) => {
            setProduct({ ...product, sizes: value.map((option: ProductOption) => ({
              id: option.id,
              name: option.name,
              price: option.price,
              code: '',
            })) as Size[] });
          }}
        />
      </FormControl>
      <SizeComponent sizes={product.sizes} onChangePrice={(id: number, price: number) => {
        setProduct({ ...product, sizes: product.sizes.map((size) => size.id === id ? { ...size, price } : size) });
      }} />
      <FormControl sx={{ mt: 2, width: '100%' }}>
        <FormLabel htmlFor="materials">Nguyên vật liệu</FormLabel>
        <Autocomplete
          multiple
          options={materials}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => <TextField {...params} placeholder="Chọn vật liệu" />}
          value={product.materials.map((material) => ({
            id: material.id,
            name: material.name,
            price: material.price,
            code: '',
          }))}
          onChange={(event, value) => {
            setProduct({ ...product, materials: value.map((option: ProductOption) => ({
              id: option.id,
              name: option.name,
              price: option.price,
              code: '',
            })) as Material[] });
          }}
        />
      </FormControl>
      <MaterialComponent materials={product.materials} onChangePrice={(id: number, price: number) => {
        setProduct({ ...product, materials: product.materials.map((material) => material.id === id ? { ...material, price } : material) });
      }} />
      <FormControl sx={{ mt: 2, width: '100%' }}>
        <FormLabel htmlFor="addons">Phụ kiện</FormLabel>
        <Autocomplete
          multiple
          options={addons}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => <TextField {...params} placeholder="Chọn phụ kiện" />}
          value={product.addons.map((addon) => ({
            id: addon.id,
            name: addon.name,
            price: addon.price,
            code: '',
          }))}
          onChange={(event, value) => {
            setProduct({ ...product, addons: value.map((option: ProductOption) => ({
              id: option.id,
              name: option.name,
              price: option.price,
              code: '',
            })) as Addon[] });
          }}
        />
      </FormControl>
      <AddonComponent addons={product.addons} onChangePrice={(id: number, price: number) => {
        setProduct({ ...product, addons: product.addons.map((addon) => addon.id === id ? { ...addon, price } : addon) });
      }} />
      <FormControl sx={{ mt: 2, width: '100%' }}>
        <FormLabel htmlFor="photos">Hình ảnh sản phẩm</FormLabel>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, my: 2 }}>
          {product.photos.map((photo, index) => (
            <ShireImageCard
              key={`product-photo-${photo.id}-${index}`}
              id={photo.id}
              title={"Hình ảnh sản phẩm"}
              description={"Hình ảnh sản phẩm"}
              imageUrl={photo.url}
              isActive={photo.isActive}
              onToggleActive={() => {}}
              onDelete={() => {}}
              onEdit={() => {}}
            />
          ))}
        </Box>
        <ShireImageUpload
          file={imageFile}
          setFile={setImageFile}
          minWidth={100}
          minHeight={100}
          maxWidth={1000}
          maxHeight={1000}
          label="Tải lên hình ảnh sản phẩm"
          accept="image/jpeg,image/png,image/webp,image/jpg"
        />
        {imageFile && <Button onClick={handleAddPhoto}>Thêm hình ảnh</Button>}
      </FormControl>
      <Box mt={2} display="flex" justifyContent="flex-end">
        <Button onClick={onClose} sx={{ mr: 1 }}>Hủy</Button>
        <Button type="submit" variant="contained" color="primary">{isEdit ? 'Cập nhật' : 'Thêm'}</Button>
      </Box>
    </Box>
  </Modal>;
};

const SizeComponent = ({ sizes, onChangePrice }: { sizes: Size[], onChangePrice: (id: number, price: number) => void }) => {
  return <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 2 }}>
    {sizes.map((size) => (
      <Box key={size.id} sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel htmlFor="size-name">Giá của {size.name}</FormLabel>
          <TextField value={size.price} type="number" onChange={(e) => onChangePrice(size.id, Number(e.target.value))} />
        </FormControl>
      </Box>
    ))}
  </Box>
};

const MaterialComponent = ({ materials, onChangePrice }: { materials: Material[], onChangePrice: (id: number, price: number) => void }) => {
  return <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 2 }}>
    {materials.map((material) => (
      <Box key={material.id} sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel htmlFor="material-name">Giá của {material.name}</FormLabel>
          <TextField value={material.price} type="number" onChange={(e) => onChangePrice(material.id, Number(e.target.value))} />
        </FormControl>
      </Box>
    ))}
  </Box>
};

const AddonComponent = ({ addons, onChangePrice }: { addons: Addon[], onChangePrice: (id: number, price: number) => void }) => {
  return <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 2 }}>
    {addons.map((addon) => (
      <Box key={addon.id} sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel htmlFor="addon-name">Giá của {addon.name}</FormLabel>
          <TextField value={addon.price} type="number" onChange={(e) => onChangePrice(addon.id, Number(e.target.value))} />
        </FormControl>
      </Box>
    ))}
  </Box>
};